<template>
  <v-slide-x-transition hide-on-leave>
    <v-hover v-if="editable && !localEditing"
             v-slot="{ hover }"
             :disabled="readOnly">
      <v-row :class="{'ma-0' : true, 'grey lighten-5 reg-editable-side-item': hover}"
             @click="onEditUsersClick">
        <v-icon class="mr-1">
          mdi-account
        </v-icon>
        <div>
          <v-chip v-for="user in selectedUsers"
                  :key="user.id"
                  class="chip--select-multi">
            <reg-user-avatar :user="user" />
            <span>{{ user.name }}</span>
          </v-chip>
        </div>
      </v-row>
    </v-hover>
    <div v-else :class="{'pa-2 elevation-1 grey lighten-4': editable}">
      <v-row v-if="editable" class="ma-0">
        <v-spacer />
        <v-btn text
               depressed
               class="ma-0"
               @click.stop="onCancelEditClick">
          <span>{{ $t('cancel') }}</span>
        </v-btn>
        <v-btn text
               depressed
               color="primary"
               class="ma-0"
               @click.stop="onSaveEditClick">
          <v-icon class="mr-1">
            mdi-content-save
          </v-icon>
          <span>{{ $t('save') }}</span>
        </v-btn>
      </v-row>
      <component :is="allowNew ? 'v-combobox' : 'v-autocomplete'"
                 ref="input"
                 :items="filteredUsers"
                 :disabled="disabled"
                 :single-line="singleLine"
                 :hide-details="hideDetails"
                 :autofocus="autofocus"
                 :value="valueClone"
                 :label="label"
                 :placeholder="placeholder"
                 :clearable="clearable"
                 :hint="hint"
                 :persistent-hint="persistentHint"
                 :required="required"
                 :multiple="multiple"
                 :rules="rules"
                 :return-object="returnObject || allowNew"
                 :class="cssClass"
                 :filter="searchUserFunction"
                 chips
                 prepend-icon="mdi-account"
                 item-text="name"
                 item-value="id"
                 @input="onAutoCompleteChange">
        <template #selection="data">
          <v-chip v-if="data.index < 4"
                  :value="data.selected"
                  :close="clearable"
                  class="chip--select-multi"
                  @input="remove(data.item)">
            <template v-if="data.item.id">
              <reg-user-avatar :user="data.item" />
              <span>{{ data.item.name }}</span>
            </template>
            <span v-else>{{ data.item }}</span>
          </v-chip>
          <span v-if="data.index === 4"
                class="grey--text text-caption">
            (+{{ valueClone.length - 5 }} {{ $tc('other', valueClone.length - 5) }})
          </span>
        </template>
        <template v-if="allowNew" #no-data>
          <v-list-item>
            <v-list-item-content>
              <v-list-item-title>
                {{ $t('users_module.user_will_be_invited') }}
              </v-list-item-title>
            </v-list-item-content>
          </v-list-item>
        </template>
        <template #item="{ item }">
          <v-list-item-avatar>
            <reg-user-avatar :user="item" />
          </v-list-item-avatar>
          <v-list-item-content>
            <v-list-item-title>
              {{ item.name }}
            </v-list-item-title>
            <v-list-item-subtitle>
              {{ item.username }}
            </v-list-item-subtitle>
          </v-list-item-content>
        </template>
        <v-list-item v-if="multiple"
                     slot="prepend-item"
                     ripple
                     @click="toggle">
          <v-list-item-action>
            <v-icon :color="users.length > 0 ? 'indigo darken-4' : ''">
              {{ icon }}
            </v-icon>
          </v-list-item-action>
          <v-list-item-title>{{ $t('select_all_users') }}</v-list-item-title>
        </v-list-item>
        <v-divider slot="prepend-item"
                   class="mt-2" />
      </component>
    </div>
  </v-slide-x-transition>
</template>

<script>
import UserAvatar from '~shared/UserAvatar.vue'
import VAutocomplete from 'vuetify/lib/components/VAutocomplete'
import VCombobox from 'vuetify/lib/components/VCombobox'
import { mapGetters } from 'vuex'
import _ from 'lodash'

export default {
  name: 'RegUserSelector',
  components: {
    'reg-user-avatar': UserAvatar,
    VAutocomplete,
    VCombobox
  },
  props: {
    value: {
      type: [String, Object, Number, Array],
      default: undefined
    },
    disabled: {
      type: Boolean,
      default: false
    },
    required: {
      type: Boolean,
      default: false
    },
    multiple: {
      type: Boolean,
      default: false
    },
    singleLine: {
      type: Boolean,
      default: false
    },
    hideDetails: {
      type: Boolean,
      default: false
    },
    autofocus: {
      type: Boolean,
      default: false
    },
    role: {
      type: String,
      default: ''
    },
    label: {
      type: String,
      default: ''
    },
    placeholder: {
      type: String,
      default: ''
    },
    clearable: {
      type: Boolean,
      default: false
    },
    hint: {
      type: String,
      default: ''
    },
    persistentHint: {
      type: Boolean,
      default: false
    },
    rules: {
      type: Array,
      default: () => {
        return []
      }
    },
    returnObject: {
      type: Boolean,
      default: false
    },
    editable: {
      type: Boolean,
      default: false
    },
    readOnly: {
      type: Boolean,
      default: false
    },
    editing: {
      type: Boolean,
      default: false
    },
    projectMembers: {
      type: Boolean,
      default: false
    },
    allowNew: {
      type: Boolean,
      default: false
    },
    cssClass: {
      type: String,
      default: ''
    }
  },
  data() {
    return {
      valueClone: null,
      localEditing: false
    }
  },
  computed: {
    ...mapGetters(['users_getter', 'selectedProjectMembers']),
    users() {
      return this.projectMembers ? this.selectedProjectMembers : this.users_getter
    },
    allUsers() {
      return this.multiple && this.valueClone && this.valueClone.length === this.users.length
    },
    someUsers() {
      return this.multiple && this.users.length > 0 && !this.allUsers
    },
    icon() {
      if (this.allUsers) return 'mdi-close-box'
      if (this.someUsers) return 'mdi-minus-box'
      return 'mdi-checkbox-blank-outline'
    },
    filteredUsers() {
      if (this.role) {
        return _.filter(this.users, user => {
          return user.role === this.role
        })
      } else {
        return this.users || []
      }
    },
    selectedUsers() {
      if (this.returnObject) {
        return [this.valueClone]
      } else {
        return _.filter(this.users, user => {
          if (_.isArray(this.valueClone)) {
            return _.findIndex(this.valueClone, userId => {
              return user.id === userId
            }) !== -1
          }
          return user.id === this.valueClone
        })
      }
    },
    hasError() {
      return this.$refs.input?.hasError
    }
  },
  watch: {
    value: {
      handler: 'onValueChanged',
      immediate: true
    },
    editing: {
      handler: 'setEditing',
      immediate: true
    }
  },
  methods: {
    onCancelEditClick() {
      this.valueClone = _.cloneDeep(this.value)
      this.setEditing(false)
      this.$emit('cancel')
    },
    onSaveEditClick() {
      this.updateValue(this.valueClone)
      this.setEditing(false)
    },
    onEditUsersClick() {
      if (!this.readOnly) {
        this.setEditing(true)
      }
    },
    onValueChanged(value) {
      this.setEditing(false)
      if (this.multiple) {
        this.valueClone = _.cloneDeep(!_.isArray(value) ? [value] : value)
      } else {
        this.valueClone = _.cloneDeep(_.isArray(value) ? value[0] : value)
      }
    },
    remove(item) {
      const index = this.valueClone.indexOf(item.id)
      if (index >= 0) this.valueClone.splice(index, 1)
      if (!this.editable) {
        this.updateValue(this.valueClone)
      }
    },
    onAutoCompleteChange(newValue) {
      this.valueClone = newValue
      if (!this.editable) {
        this.updateValue(this.valueClone)
      }
    },
    updateValue(value) {
      this.$emit('input', value)
    },
    toggle() {
      this.$nextTick(() => {
        if (this.allUsers) {
          this.valueClone = []
        } else {
          this.valueClone = _.map(this.users, user => {
            return this.returnObject ? user : user.id
          })
        }
        if (!this.editable) {
          this.updateValue(this.valueClone)
        }
      })
    },
    setEditing(value) {
      this.localEditing = value
      this.$emit('editing', value)
    },
    searchUserFunction(item, queryText, itemText) {
      const nameWithoutAccents = itemText.normalize('NFD').replace(/[\u0300-\u036f]/g, '')
      const nameFound = itemText.toLocaleLowerCase().indexOf(queryText.toLocaleLowerCase()) > -1
      const nameWithoutAccentsFound = nameWithoutAccents.toLocaleLowerCase().indexOf(queryText.toLocaleLowerCase()) > -1
      const emailFound = item.email.toLocaleLowerCase().indexOf(queryText.toLocaleLowerCase()) > -1
      return nameFound || emailFound || nameWithoutAccentsFound
    }
  }
}
</script>
